import {
  ActionItem,
  FeedEvent,
  Process,
  Unit,
} from '@urbanmix-tech/shared-js';
import { comparePseudeDates } from '@/helpers/common';
import type { UnitStatusView } from '@/helpers/unitStatus';

export type DisplayProcess = {
  process: Process;
  completionEvent?: FeedEvent;
  lastEvent?: FeedEvent;
  outcome?: string;
  lastActionItem?: ActionItem;
};

/**
 * Analyzes unit processes and organizes them into active and completed groups
 * with associated event and action item data
 */
export function analyzeUnitProcesses(
  unit: Unit,
  processes: Process[],
  events: FeedEvent[],
  actionItems: ActionItem[]
): DisplayProcess[] {
  const activeProcesses: Process[] = processes.filter(
    (p: Process) => p.status !== 'completed' && p.status !== 'cancelled'
  );

  const completedProcesses = processes
    .filter((p: Process) => p.status === 'completed')
    .sort((a: Process, b: Process) =>
      comparePseudeDates(a.completedAt, b.completedAt)
    );

  let displayProcesses: DisplayProcess[] = [];

  if (activeProcesses.length > 0) {
    const analyzedProcesses = activeProcesses.map((p: Process) => {
      const processEvents = events
        .filter((e) => e.processId === p.id)
        .sort((a: FeedEvent, b: FeedEvent) =>
          comparePseudeDates(a.eventDate, b.eventDate)
        );

      const lastEvent = processEvents[processEvents.length - 1];

      const lastActionItem = actionItems
        .filter((ai) => ai.processId === p.id && ai.status === 'active')
        .sort((a: ActionItem, b: ActionItem) =>
          comparePseudeDates(a.createDate, b.createDate)
        )
        .reverse()[0];

      return {
        process: p,
        lastEvent: lastEvent,
        lastActionItem,
      };
    });

    displayProcesses = [...analyzedProcesses];
  }

  if (completedProcesses.length > 0) {
    const analyzedProcesses = completedProcesses.map((p: Process) => {
      const processEvents = events
        .filter((e) => e.processId === p.id)
        .sort((a: FeedEvent, b: FeedEvent) =>
          comparePseudeDates(a.eventDate, b.eventDate)
        );

      const completionEvent = processEvents.find(
        (e: FeedEvent) => e.eventData.type === 'completion'
      );
      const lastEvent = processEvents[processEvents.length - 1];

      return {
        process: p,
        completionEvent: completionEvent,
        lastEvent: lastEvent,
        outcome: completionEvent ? completionEvent.eventData.title : '',
      };
    });
    displayProcesses = [...displayProcesses, ...analyzedProcesses];
  }

  return displayProcesses;
}

export type RenewalCategory = 'renewing' | 'moving_out' | 'no_process';

/**
 * Which bucket a unit falls into on the Renewal Status page.
 *
 * Reads BOTH signals, because neither covers the building on its own:
 *
 * - **Processes** are the operational truth and by far the better populated —
 *   they exist for ~3x more units than status events do, and only they know the
 *   fine-grained step ("waiting for tenant signature").
 * - **Status events** (`state_*`) know apartment-level facts no process tracks:
 *   that a tenant actually gave notice, or that the renewal was executed.
 *
 * Driving this purely off `state_*` would blank most of the page; ignoring them
 * would miss renewals and notices recorded outside a process. So: either source
 * can put a unit in a bucket.
 *
 * Precedence keeps `renewing` above `moving_out`, matching the previous
 * process-only behaviour — changing it would silently re-color the 3D model and
 * re-bucket the status filter.
 */
export function deriveRenewalCategory(
  displayProcess: DisplayProcess[],
  status?: UnitStatusView
): RenewalCategory {
  const hasRenewalProcess = displayProcess.some((p) => p.process.type === 'lease_renewal');
  const hasMoveOutProcess = displayProcess.some((p) => p.process.type === 'move_out');

  const renewingByStatus = !!status?.current?.renewal || !!status?.current?.renewed;
  const movingOutByStatus = !!status?.current?.moveOut || status?.occupancy === 'vacant';

  if (hasRenewalProcess || renewingByStatus) return 'renewing';
  if (hasMoveOutProcess || movingOutByStatus) return 'moving_out';
  return 'no_process';
}

/**
 * The fine-grained line shown after the category chip. Process-only by design:
 * this is the "what am I waiting on" detail, which lives in action items and has
 * no equivalent in the status feed.
 */
export function renewalStatusLabel(displayProcess: DisplayProcess[]): string {
  const renewalProcess = displayProcess.find((p) => p.process.type === 'lease_renewal');
  const moveOutProcess = displayProcess.find((p) => p.process.type === 'move_out');
  const process = renewalProcess ?? moveOutProcess;

  if (!process) return 'No Process';

  if (process.process.status === 'completed') {
    return (
      process.outcome ||
      (process === renewalProcess ? 'Renewal Completed' : 'Move-Out Completed')
    );
  }
  return (
    process.lastActionItem?.title ||
    process.process.name ||
    (process === renewalProcess ? 'Renewing' : 'Moving Out')
  );
}

export const steps: Record<string, number> = {
  waiting_to_send: 1,
  waiting_for_response: 2,
  waiting_for_tenant_signature: 3,
  waiting_for_countersignature: 4,
  upload_lease: 5,
};

export const stepToStatus: Record<number, string> = {
  1: 'Started',
  2: 'Sent to tenant',
  3: 'Tenant signed',
  4: 'Completed',
  5: 'Completed',
};
